import { useCallback, useState } from 'react';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { MarkdownEditor } from './MarkdownEditor';
import { FormSection } from '../FormSection';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { useIntl } from 'react-intl';
import {
  getGetTenantQueryKey,
  TenantDTO,
  TenantUpdateDTO,
  useTenantUpdate,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast.tsx';
import { trimData } from '@/helpers/trimData';
import { useQueryClient } from '@tanstack/react-query';
import { checkUserPermissions } from '../Auth/checkUserPermissions';

const tenantSettingsSupportScheme = z.object({
  markdown: z.string(),
});

type TenantSettingsSupportType = z.infer<typeof tenantSettingsSupportScheme>;

export type SupportMarkdownKey =
  | 'faq_company'
  | 'faq_school'
  | 'terms_and_conditions'
  | 'privacy_policy'
  | 'support';

interface TenantSettingsSupportFormProps {
  title: string;
  description?: string;
  markdown: string;
  tenant: TenantDTO;
  markdownKey: SupportMarkdownKey;
}

export const TenantSettingsSupportForm = ({
  title,
  description,
  markdown,
  markdownKey,
}: TenantSettingsSupportFormProps) => {
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  const { formatMessage } = useIntl();
  const tenantUpdate = useTenantUpdate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [markdownContent, setMarkdownContent] = useState<string>(
    markdown ||
      formatMessage({
        defaultMessage: 'Saknas',
      }),
  );

  const defaultValues = {
    markdown: markdownContent,
  };

  const { handleSubmit, control, reset } = useForm<TenantSettingsSupportType>({
    defaultValues,
    resolver: zodResolver(tenantSettingsSupportScheme, {
      errorMap: customErrorMap(),
    }),
    mode: 'onBlur',
  });

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    reset();
    setEditMode(false);
  }, [reset]);

  const onSubmit: SubmitHandler<TenantSettingsSupportType> = useCallback(
    (data) => {
      const submitData: TenantUpdateDTO = trimData({
        [markdownKey]: data.markdown,
      });

      tenantUpdate.mutate(
        { data: submitData },
        {
          onSuccess: async (updatedTenant) => {
            toast.success(
              {
                title: formatMessage(
                  { defaultMessage: '{title} uppdaterades' },
                  { title },
                ),
              },
              { autoClose: 3000 },
            );
            setMarkdownContent(updatedTenant[markdownKey]);
            await queryClient.invalidateQueries({
              queryKey: getGetTenantQueryKey(),
            });
            setEditMode(false);
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: formatMessage({
                  defaultMessage: 'Ett fel inträffade',
                }),
                description: formatMessage({
                  defaultMessage:
                    'Försök spara igen senare, eller vänligen kontakta support.',
                }),
              },
              { autoClose: 3000 },
            );
            return;
          },
        },
      );
    },
    [formatMessage, markdownKey, queryClient, tenantUpdate, title],
  );

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <FormSection
        title={title}
        description={description}
        onClickButton={startEdit}
        editMode={editMode}
        canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
      >
        <MarkdownEditor
          name="markdown"
          control={control}
          isOpen={editMode}
          onCancel={cancelEdit}
        />
      </FormSection>
    </form>
  );
};
